/*
 * @providesModule SKZPersonalInfo
 *
 */

import _ from 'lodash';
import Countries from 'Countries';
import {LoyaltyStoreOrderShipping} from 'SKZLoyalty';

type AddressType = 'SHIPPING' | 'BILLING';
export type EmailData = {
  email: string;
  verified: boolean;
};

export class PersonalInfoRecord {
  first_name: string;
  last_name: string;
  birth_date: string;

  phone_number: string;
  phone_verified: boolean;

  email: string;
  email_verified: boolean;
  secondary_emails: Array<EmailData>;

  billing_address: {
    first_name: string;
    last_name: string;
    address_line1: string;
    address_line2: string;
    city: string;
    state: string;
    country: string;
    zip: string;
  };

  shipping_address: {
    first_name: string;
    last_name: string;
    address_line1: string;
    address_line2: string;
    city: string;
    state: string;
    country: string;
    zip: string;
  };
}

export default class PersonalInfo extends PersonalInfoRecord {
  constructor(keyValueSource?: Object) {
    super();

    if (_.get(keyValueSource, 'secondary_emails', []).length > 0) {
      keyValueSource.secondary_emails.sort((a: Object, b: Object): boolean => {
        return a.email > b.email;
      });
    }

    return _.create(PersonalInfo.prototype, keyValueSource);
  }

  hasAddress(addressType: AddressType = 'SHIPPING'): boolean {
    const address = addressType == 'SHIPPING' ? this.shipping_address : this.billing_address;
    if (!_.isEmpty(address)) {
      let hasValue = false;
      const keys = _.keys(address);
      for (let i = 0; i < keys.length; i++) {
        hasValue |= address[keys[i]] != null;
      }
      return hasValue;
    }
    return false;
  }

  formatAddress(addressType: AddressType = 'SHIPPING'): string {
    const address = addressType == 'SHIPPING' ? this.shipping_address : this.billing_address;
    const formatAddress = [];
    if (address.first_name || address.last_name) {
      const name = [];
      if (address.first_name) {
        name.push(address.first_name);
      }
      if (address.last_name) {
        name.push(address.last_name);
      }
      formatAddress.push(name.join(' '));
    }
    if (address.address_line1) {
      formatAddress.push(address.address_line1);
    }
    if (address.address_line2) {
      formatAddress.push(address.address_line2);
    }
    if (address.city || address.state || address.zip) {
      let line = [];
      if (address.city) {
        line.push(address.city);
      }
      if (address.state) {
        line.push(address.state);
      }
      if (line.length) {
        line = [line.join(', ')];
      }
      if (address.zip) {
        line.push(address.zip);
      }
      formatAddress.push(line.join(' '));
    }
    if (address.country) {
      formatAddress.push(Countries.getCountryNameByCode(address.country) || address.country);
    }

    return formatAddress.join('\n');
  }

  updateAddressInfo(addressInfo: LoyaltyStoreOrderShipping) {
    this.shipping_address.first_name = addressInfo.first_name;
    this.shipping_address.last_name = addressInfo.last_name;
    this.shipping_address.address_line1 = addressInfo.street_address_line_1;
    this.shipping_address.address_line2 = addressInfo.street_address_line_2 ?? '';
    this.shipping_address.city = addressInfo.city;
    this.shipping_address.state = addressInfo.region;
    this.shipping_address.country = addressInfo.country_code;
    this.shipping_address.zip = addressInfo.postal_code ?? '';
    this.phone_number = addressInfo.phone;
  }

  convertToLoyaltyShipping(): LoyaltyStoreOrderShipping {
    return {
      first_name: this.shipping_address?.first_name ?? '',
      last_name: this.shipping_address?.last_name ?? '',
      email: this.email,
      phone: this.phone_number ?? '',
      street_address_line_1: this.shipping_address?.address_line1 ?? '',
      street_address_line_2: this.shipping_address?.address_line2 ?? null,
      city: this.shipping_address?.city ?? '',
      region: this.shipping_address?.state ?? '',
      country_code: this.shipping_address?.country ?? '',
      postal_code: this.shipping_address?.zip ?? null,
    };
  }

  getAllUserEmails(): Array<EmailData> {
    const emails = [];

    if (this.email) {
      emails.push({email: this.email, verified: this.email_verified});
    }

    if (this.secondary_emails) {
      this.secondary_emails.forEach((emailItem: Object) => {
        emails.push(emailItem);
      });
    }

    return emails;
  }

  getUserPrimaryEmailData(): EmailData {
    if (!this.email) {
      return {
        email: undefined,
        verified: false,
      };
    }

    return {
      email: this.email,
      verified: this.email_verified,
    };
  }

  getUserSecondaryEmails(): Array<EmailData> {
    return _.get(this, 'secondary_emails', []) || [];
  }

  generateEmailMap(): Map<string, EmailData> {
    const map = new Map();
    this.getUserSecondaryEmails().forEach((emailItem: Object) => {
      map.set(emailItem.email, emailItem);
    });

    return map;
  }

  hasVerifiedAnEmail(): boolean {
    const verifiedSecondaryEmail = _.find(this.getUserSecondaryEmails(), 'verified');

    return this.email_verified || !!verifiedSecondaryEmail;
  }

  hasVerifiedAllEmails(): boolean {
    const unverifiedEmails = _.find(this.getUserSecondaryEmails(), (email: Object): boolean => {
      return !email.verified;
    });

    return this.email_verified && !unverifiedEmails;
  }

  isPhoneNumberSaved(): boolean {
    return this.phone_number != null && this.phone_number != '';
  }

  isPhoneNumberVerified(): boolean {
    return this.phone_verified;
  }

  getPhoneNumber(): string {
    return this.phone_number;
  }
}
